import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, Subscription } from 'rxjs';
import { delay, expand } from 'rxjs/operators';

import { SharedService } from '@app/core';
import { Game } from '@courses/entities';
import { GameService } from './game.service';

export interface SimulationState {
  isSimulating: boolean;
  currentTaskId: string | null;
  currentGame: Game | null;
}

// Configuration constants
const MAX_SIMULATION_PERIOD = 14;
const MIN_ROLLBACK_PERIOD = 0;

@Injectable({
  providedIn: 'root',
})
export class SimulationService {
  private simulationStateSubject = new BehaviorSubject<SimulationState>({
    isSimulating: false,
    currentTaskId: null,
    currentGame: null,
  });

  public simulationState$ = this.simulationStateSubject.asObservable();
  private currentPollingSubscription: Subscription | null = null;

  constructor(
    private gameService: GameService,
    private sharedService: SharedService
  ) {}

  /**
   * Starts a simulation for the specified course and game
   * Cancels any existing simulation polling before starting new one
   */
  startSimulation(courseId: number, gameId: number): Observable<any> {
    const currentGame = this.simulationStateSubject.value.currentGame;

    // Validate that we haven't exceeded the maximum period
    if (currentGame && currentGame.period >= MAX_SIMULATION_PERIOD) {
      const errorMessage = `Cannot simulate beyond period ${MAX_SIMULATION_PERIOD}. Current period: ${currentGame.period}`;
      this.sharedService.errorAlert(errorMessage);
      return new Observable(observer => {
        observer.error(new Error(errorMessage));
      });
    }

    // Cancel any existing polling operation
    this.cancelCurrentPolling();

    // OPTIMISTIC UPDATE: Immediately increment period in UI
    const optimisticGame = currentGame
      ? new Game({
          id: currentGame.id,
          name: currentGame.name,
          period: currentGame.period + 1,
          isAssign: currentGame.isAssign,
          type: currentGame.type,
          status: currentGame.status,
          owner: currentGame.owner,
          iconfile: currentGame.icon,
          isSimulating: currentGame.isSimulating,
          lastUpdate: currentGame.lastUpdatedOn?.toISOString() || null,
        } as any)
      : null;

    // Update state to indicate simulation is starting with optimistic game state
    this.updateSimulationState({
      isSimulating: true,
      currentTaskId: null,
      currentGame: optimisticGame,
    });

    return new Observable(observer => {
      this.gameService.simulate(courseId, gameId).subscribe({
        next: (res: any) => {
          this.sharedService.successAlert(
            'The game is queued to simulate to next period successfully!'
          );

          // Update state with task ID (keep optimistic game state)
          this.updateSimulationState({
            isSimulating: true,
            currentTaskId: res.taskId,
            currentGame: optimisticGame,
          });

          // Start polling for task completion
          this.startTaskPolling(courseId, res.taskId, currentGame);

          observer.next(res);
          observer.complete();
        },
        error: (error: any) => {
          console.log('Simulation error:', error);
          // REVERT OPTIMISTIC UPDATE on error
          this.updateSimulationState({
            isSimulating: false,
            currentTaskId: null,
            currentGame: currentGame, // Revert to original game state
          });
          this.sharedService.errorAlert(error);
          observer.error(error);
        },
      });
    });
  }

  /**
   * Starts polling for task completion
   */
  private startTaskPolling(courseId: number, taskId: string, originalGame?: Game | null): void {
    this.currentPollingSubscription = this.gameService
      .getTask(courseId, taskId)
      .pipe(
        expand((res: any) =>
          res.status !== 'SUCCESS'
            ? this.gameService.getTask(courseId, taskId).pipe(delay(5000))
            : EMPTY
        )
      )
      .subscribe({
        next: (res: any) => {
          if (res.status === 'SUCCESS') {
            console.log('Simulation polling completed successfully');
            const updatedGame = new Game(res.data);

            // Update state with completed game
            this.updateSimulationState({
              isSimulating: false,
              currentTaskId: null,
              currentGame: updatedGame,
            });

            this.currentPollingSubscription = null;

            // Show success alert
            this.sharedService.successAlert('Simulation completed successfully!');
          }
        },
        error: (error: any) => {
          console.log('Task polling error:', error);
          // REVERT OPTIMISTIC UPDATE on polling error
          this.updateSimulationState({
            isSimulating: false,
            currentTaskId: null,
            currentGame: originalGame || null,
          });
          this.sharedService.errorAlert(error);
        },
      });
  }

  /**
   * Cancels current polling operation if exists
   */
  private cancelCurrentPolling(): void {
    if (this.currentPollingSubscription) {
      this.currentPollingSubscription.unsubscribe();
      this.currentPollingSubscription = null;
      console.log('Previous simulation polling cancelled');
    }
  }

  /**
   * Updates the current simulation state
   */
  private updateSimulationState(newState: SimulationState): void {
    this.simulationStateSubject.next(newState);
  }

  /**
   * Resets simulation state to initial values
   */
  private resetSimulationState(): void {
    this.updateSimulationState({
      isSimulating: false,
      currentTaskId: null,
      currentGame: this.simulationStateSubject.value.currentGame,
    });
    this.currentPollingSubscription = null;
  }

  /**
   * Gets current simulation state
   */
  get currentState(): SimulationState {
    return this.simulationStateSubject.value;
  }

  /**
   * Checks if simulation is currently in progress
   */
  get isSimulating(): boolean {
    return this.simulationStateSubject.value.isSimulating;
  }

  /**
   * Gets the current game being simulated
   */
  get currentGame(): Game | null {
    return this.simulationStateSubject.value.currentGame;
  }

  /**
   * Validates if a rollback to the specified period is allowed
   */
  canRollbackToPeriod(targetPeriod: number): { canRollback: boolean; errorMessage?: string } {
    const currentGame = this.simulationStateSubject.value.currentGame;

    if (!currentGame) {
      return { canRollback: false, errorMessage: 'No game loaded' };
    }

    if (targetPeriod < MIN_ROLLBACK_PERIOD) {
      return {
        canRollback: false,
        errorMessage: `Cannot rollback to period ${targetPeriod}. Minimum allowed period is ${MIN_ROLLBACK_PERIOD}`,
      };
    }

    if (targetPeriod > MAX_SIMULATION_PERIOD) {
      return {
        canRollback: false,
        errorMessage: `Cannot rollback to period ${targetPeriod}. Maximum allowed period is ${MAX_SIMULATION_PERIOD}`,
      };
    }

    if (targetPeriod >= currentGame.period) {
      return {
        canRollback: false,
        errorMessage: `Cannot rollback to period ${targetPeriod}. Current period is ${currentGame.period}`,
      };
    }

    return { canRollback: true };
  }

  /**
   * Gets the maximum allowed simulation period
   */
  get maxSimulationPeriod(): number {
    return MAX_SIMULATION_PERIOD;
  }

  /**
   * Gets the minimum allowed rollback period
   */
  get minRollbackPeriod(): number {
    return MIN_ROLLBACK_PERIOD;
  }

  /**
   * Checks if simulation is allowed for the current game
   */
  canSimulate(): boolean {
    const currentGame = this.simulationStateSubject.value.currentGame;
    return currentGame ? currentGame.period < MAX_SIMULATION_PERIOD : false;
  }

  /**
   * Checks if rollback is allowed for the current game
   */
  canRollback(): boolean {
    const currentGame = this.simulationStateSubject.value.currentGame;
    return currentGame ? currentGame.period > 0 : false;
  }

  /**
   * Sets the current game (called when game data is loaded)
   */
  setCurrentGame(game: Game): void {
    this.updateSimulationState({
      ...this.simulationStateSubject.value,
      currentGame: game,
    });
  }

  /**
   * Cleanup method - should be called on component destruction
   */
  cleanup(): void {
    this.cancelCurrentPolling();
    this.resetSimulationState();
  }
}
